import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Users, TrendingUp, TrendingDown, Sparkles, Search, Filter,
  ArrowUpDown, Download, Plus, Check, MoreHorizontal,
} from "lucide-react";

import { AdminShell } from "@/components/admin/admin-shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Money, Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/admin/plans/")({
  head: () => ({
    meta: [
      { title: "Subscription Management — Spayzo Admin" },
      { name: "description", content: "Manage subscription plans and monitor subscriber metrics." },
    ],
  }),
  component: Page,
});

type SubStatus = "Active" | "Trial" | "Paused" | "Expired";
type PlanKey = "starter" | "growth" | "premium";
type CycleKey = "monthly" | "annual";
const SUBS: {
  merchant: string; location: string; id: string; plan: PlanKey;
  status: SubStatus; cycle: CycleKey; renewal: string; amount: React.ReactNode; joined: string;
}[] = [
  { merchant: "Amber Lounge", location: "Dubai Marina", id: "MRC-0041", plan: "growth", status: "Active", cycle: "monthly", renewal: "Jul 14, 2025", amount: <Money value="249" />, joined: "Jan 14, 2024" },
  { merchant: "The Zen Garden", location: "Downtown Dubai", id: "MRC-0082", plan: "premium", status: "Active", cycle: "annual", renewal: "Mar 02, 2026", amount: <Money value="5,988" />, joined: "Mar 02, 2024" },
  { merchant: "Coco Beach Club", location: "JBR Beach", id: "MRC-0117", plan: "starter", status: "Trial", cycle: "monthly", renewal: "Jun 28, 2025", amount: <Money value="0" />, joined: "Jun 14, 2025" },
  { merchant: "Spice Route", location: "Business Bay", id: "MRC-0063", plan: "growth", status: "Paused", cycle: "monthly", renewal: "—", amount: <Money value="249" />, joined: "Aug 08, 2023" },
  { merchant: "Luna Rooftop", location: "DIFC", id: "MRC-0095", plan: "premium", status: "Expired", cycle: "annual", renewal: "—", amount: <Money value="5,988" />, joined: "Nov 19, 2022" },
  { merchant: "Surf Social", location: "JBR Walk", id: "MRC-0134", plan: "starter", status: "Active", cycle: "monthly", renewal: "Jul 03, 2025", amount: <Money value="99" />, joined: "Jun 03, 2025" },
];

const STATUS_STYLES: Record<SubStatus, string> = {
  Active: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  Trial: "bg-sky-500/10 text-sky-700 border-sky-500/20",
  Paused: "bg-amber-500/10 text-amber-700 border-amber-500/20",
  Expired: "bg-rose-500/10 text-rose-700 border-rose-500/20",
};

function Page() {
  const { t } = useTranslation();

  const KPIS = [
    { label: t("adminPlans.kpis.subscribers"), value: "2,841", sub: t("adminPlans.kpis.subscribersSub"), icon: Users, trend: "+8.4%", up: true },
    { label: t("adminPlans.kpis.mrr"), value: <Money value="128K" />, sub: t("adminPlans.kpis.mrrSub"), icon: TrendingUp, trend: t("adminPlans.mrrLabel"), up: true },
    { label: t("adminPlans.kpis.churn"), value: "3.2%", sub: t("adminPlans.kpis.churnSub"), icon: TrendingDown, trend: t("adminPlans.kpis.churnTrend"), up: false },
    { label: t("adminPlans.kpis.trials"), value: "184", sub: t("adminPlans.kpis.trialsSub"), icon: Sparkles, trend: t("adminPlans.kpis.trialsTrend"), up: true },
  ];

  const PLANS = [
    {
      key: "starter" as PlanKey, name: t("adminPlans.planNames.starter"), price: 99, badge: null,
      blurb: t("adminPlans.plans.starterBlurb"),
      subs: 842, mrr: <Money value="83K" />, churn: "4.1%",
      features: [t("adminPlans.plans.starterF1"), t("adminPlans.plans.starterF2"), t("adminPlans.plans.starterF3")],
    },
    {
      key: "growth" as PlanKey, name: t("adminPlans.planNames.growth"), price: 249, badge: "MOST POPULAR",
      blurb: t("adminPlans.plans.growthBlurb"),
      subs: 1284, mrr: <Money value="319K" />, churn: "2.8%",
      features: [t("adminPlans.plans.growthF1"), t("adminPlans.plans.growthF2"), t("adminPlans.plans.growthF3")],
    },
    {
      key: "premium" as PlanKey, name: t("adminPlans.planNames.premium"), price: 599, badge: null,
      blurb: t("adminPlans.plans.premiumBlurb"),
      subs: 715, mrr: <Money value="428K" />, churn: "1.9%",
      features: [t("adminPlans.plans.premiumF1"), t("adminPlans.plans.premiumF2"), t("adminPlans.plans.premiumF3")],
    },
  ];

  const PLAN_LABELS: Record<PlanKey, string> = {
    starter: t("adminPlans.planNames.starter"),
    growth: t("adminPlans.planNames.growth"),
    premium: t("adminPlans.planNames.premium"),
  };
  const CYCLE_LABELS: Record<CycleKey, string> = {
    monthly: t("adminPlans.cycle.monthly"),
    annual: t("adminPlans.cycle.annual"),
  };

  const STATUS_LABELS: Record<SubStatus, string> = {
    Active: t("adminPlans.status.active"),
    Trial: t("adminPlans.status.trial"),
    Paused: t("adminPlans.status.paused"),
    Expired: t("adminPlans.status.expired"),
  };

  const tabs = ["All", "Active", "Trial", "Expired", "Paused"] as const;
  const TAB_LABELS: Record<(typeof tabs)[number], string> = {
    All: t("adminPlans.tabs.all"),
    Active: t("adminPlans.status.active"),
    Trial: t("adminPlans.status.trial"),
    Expired: t("adminPlans.status.expired"),
    Paused: t("adminPlans.status.paused"),
  };
  const [tab, setTab] = useState<(typeof tabs)[number]>("All");
  const [query, setQuery] = useState("");

  const filtered = SUBS.filter((s) => {
    const okTab = tab === "All" || s.status === tab;
    const q = query.toLowerCase();
    const okQ = !q || s.merchant.toLowerCase().includes(q) || PLAN_LABELS[s.plan].toLowerCase().includes(q) || s.plan.includes(q);
    return okTab && okQ;
  });

  return (
    <AdminShell
      title={t("adminPlans.title")}
      subtitle={t("adminPlans.subtitle")}
    >
      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {KPIS.map((k) => (
          <Card key={k.label} className="rounded-2xl border-border/60 p-5 shadow-soft">
            <div className="flex items-center justify-between">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary">
                <k.icon className="h-4 w-4" />
              </span>
              <Badge
                variant="outline"
                className={cn(
                  "rounded-full text-[10px] font-semibold",
                  k.up ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700"
                       : "border-rose-500/30 bg-rose-500/10 text-rose-700",
                )}
              >
                {k.trend}
              </Badge>
            </div>
            <p className="mt-4 text-xs font-medium uppercase tracking-wide text-muted-foreground">{k.label}</p>
            <p className="mt-1 font-display text-3xl font-bold tracking-tight">{k.value}</p>
            <p className="mt-1 text-xs text-muted-foreground">{k.sub}</p>
          </Card>
        ))}
      </div>

      {/* Plans */}
      <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {PLANS.map((p) => {
          const featured = p.badge === "MOST POPULAR";
          return (
            <Card
              key={p.name}
              className={cn(
                "relative rounded-2xl p-6 shadow-soft",
                featured ? "border-primary/40 ring-2 ring-primary/30" : "border-border/60",
              )}
            >
              {featured && (
                <Badge className="absolute -top-3 start-6 rounded-full bg-primary text-primary-foreground hover:bg-primary">
                  ★ {t("adminPlans.mostPopular")}
                </Badge>
              )}
              <div className="flex items-center justify-between">
                <p className="font-display text-lg font-bold">{p.name}</p>
                <Badge variant="outline" className="rounded-full border-emerald-500/30 bg-emerald-500/10 text-[10px] font-semibold text-emerald-700">
                  {t("adminPlans.status.active")}
                </Badge>
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display text-3xl font-bold"><Riyal /> {p.price}</span>
                <span className="text-sm text-muted-foreground">/{t("adminPlans.perMonth")}</span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">{p.blurb}</p>

              <div className="mt-4 grid grid-cols-3 gap-3 rounded-xl border border-border/60 bg-surface p-3 text-center">
                <div>
                  <p className="font-display text-lg font-bold">{p.subs.toLocaleString()}</p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("adminPlans.subscribersLabel")}</p>
                </div>
                <div>
                  <p className="font-display text-lg font-bold">{p.mrr}</p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("adminPlans.mrrLabel")}</p>
                </div>
                <div>
                  <p className="font-display text-lg font-bold">{p.churn}</p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("adminPlans.churnLabel")}</p>
                </div>
              </div>

              <ul className="mt-4 space-y-2">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                      <Check className="h-2.5 w-2.5" />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-5">
                <Button asChild className="h-10 w-full rounded-xl" style={{ backgroundColor: "#5958F3" }}>
                  <Link to="/admin/plans/$id" params={{ id: p.key }}>{t("adminPlans.viewPlan")}</Link>
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Subscribers table */}
      <Card className="mt-8 overflow-hidden rounded-2xl border-border/60 shadow-soft">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border/60 p-5">
          <div>
            <p className="font-display text-lg font-bold">{t("adminPlans.allSubscribers")}</p>
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">2,841</span> {t("adminPlans.totalActiveMerchants")}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="h-10 rounded-xl">
              <Download className="h-4 w-4" /> {t("adminPlans.exportCsv")}
            </Button>
            <Button asChild className="h-10 rounded-xl" style={{ backgroundColor: "#5958F3" }}>
              <Link to="/admin/plans/new"><Plus className="h-4 w-4" /> {t("adminPlans.addPlan")}</Link>
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-b border-border/60 p-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("adminPlans.searchPlaceholder")}
              className="h-10 rounded-xl border-border/70 bg-surface ps-10"
            />
          </div>
          <div className="flex items-center gap-1 rounded-xl border border-border/60 bg-surface p-1">
            {tabs.map((tb) => (
              <button
                key={tb}
                onClick={() => setTab(tb)}
                className={cn(
                  "rounded-lg px-3 py-1.5 text-xs font-medium transition",
                  tab === tb ? "text-primary-foreground shadow-soft" : "text-muted-foreground hover:text-foreground",
                )}
                style={tab === tb ? { backgroundColor: "#5958F3" } : undefined}
              >
                {TAB_LABELS[tb]}
              </button>
            ))}
          </div>
          <Button variant="outline" className="h-10 rounded-xl">
            <Filter className="h-4 w-4" /> {t("adminPlans.filters")}
          </Button>
          <Button variant="outline" className="h-10 rounded-xl">
            <ArrowUpDown className="h-4 w-4" /> {t("adminPlans.sort")}
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-5 py-3 text-start font-semibold">{t("adminPlans.table.merchant")}</th>
                <th className="px-3 py-3 text-start font-semibold">{t("adminPlans.table.plan")}</th>
                <th className="px-3 py-3 text-start font-semibold">{t("adminPlans.table.status")}</th>
                <th className="px-3 py-3 text-start font-semibold">{t("adminPlans.table.billingCycle")}</th>
                <th className="px-3 py-3 text-start font-semibold">{t("adminPlans.table.nextRenewal")}</th>
                <th className="px-3 py-3 text-start font-semibold">{t("adminPlans.table.amount")}</th>
                <th className="px-3 py-3 text-start font-semibold">{t("adminPlans.table.joined")}</th>
                <th className="px-5 py-3 text-end font-semibold">{t("adminPlans.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-t border-border/60 hover:bg-surface/50">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-xs font-semibold text-primary">
                        {s.merchant.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-semibold">{s.merchant}</p>
                        <p className="truncate text-xs text-muted-foreground">{s.location} · #{s.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3.5">{PLAN_LABELS[s.plan]}</td>
                  <td className="px-3 py-3.5">
                    <Badge variant="outline" className={cn("rounded-full text-[10px] font-semibold", STATUS_STYLES[s.status])}>
                      {STATUS_LABELS[s.status]}
                    </Badge>
                  </td>
                  <td className="px-3 py-3.5">{CYCLE_LABELS[s.cycle]}</td>
                  <td className="px-3 py-3.5 text-muted-foreground">{s.renewal}</td>
                  <td className="px-3 py-3.5 font-semibold">{s.amount}</td>
                  <td className="px-3 py-3.5 text-muted-foreground">{s.joined}</td>
                  <td className="px-5 py-3.5 text-end">
                    <Button asChild variant="ghost" size="icon" className="h-8 w-8 rounded-lg">
                      <Link to="/admin/plans/$id" params={{ id: s.id }}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Link>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 p-4 text-xs text-muted-foreground">
          <span>{t("adminPlans.showingOf", { count: filtered.length })}</span>
          <div className="flex items-center gap-1">
            {["1", "2", "3", "…", "474"].map((p, i) => (
              <button
                key={i}
                className={cn(
                  "h-8 min-w-8 rounded-lg px-2 text-xs font-medium transition",
                  p === "1" ? "text-primary-foreground" : "text-muted-foreground hover:bg-surface",
                )}
                style={p === "1" ? { backgroundColor: "#5958F3" } : undefined}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </Card>
    </AdminShell>
  );
}
